import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FiX, FiDownload, FiFileText, FiBarChart2, FiCode, FiChevronDown, FiCheck, FiMail, FiSend } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import Stock from '../../../model/Stock';
import { toast } from '../Toast';
import { REPORTS, BOARD_PACK, ReportKey, RegistryData, reportTitle, reportUrl, reportCsv, saveCsv, asAtDate } from './reportsData';

interface ShellProps {
    icon: React.ReactNode;
    title: string;
    sub: string;
    note: string;
    className: string;
    onClose: () => void;
    buttons: React.ReactNode;
    hideCancel?: boolean; // the refresh progress step has only "Run in Background"
}

// the Communications pop-up look (CampaignModals.scss .campaign-modal, as ContactsModal.tsx) with a report-modal class for sizing.
// Shared with the All holders / Top holders pop-ups (HoldersModals.tsx) and the Holder detail pop-ups (HolderModals.tsx).
export const ReportModal: React.FC<ShellProps> = ({ icon, title, sub, note, className, onClose, buttons, hideCancel, children }) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return <div className="campaign-modal-overlay" onClick={onClose}>
        <div className={`campaign-modal report-modal ${className}`} role="dialog" aria-modal="true" aria-label={title} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
                <span className="icon">{icon}</span>
                <div className="t"><strong>{title}</strong><span>{sub}</span></div>
                <button type="button" className="close" aria-label="Close" onClick={onClose}><FiX /></button>
            </div>
            <div className="modal-body">{children}</div>
            <div className="modal-footer">
                <span className="note">{note}</span>
                {!hideCancel && <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>}
                {buttons}
            </div>
        </div>
    </div>;
};

export type FileType = 'pdf' | 'excel' | 'csv';
const FILE_TYPES: { key: FileType; icon: React.ReactNode; title: string; sub: string }[] = [
    { key: 'pdf', icon: <FiFileText />, title: 'PDF to read or print', sub: 'Formatted like the board pack page — logo, totals, ready to hand out' },
    { key: 'excel', icon: <FiBarChart2 />, title: 'Spreadsheet (Excel)', sub: 'Every column, so you can sort and filter it yourself' },
    { key: 'csv', icon: <FiCode />, title: 'CSV file', sub: 'Plain data for another system or your accountant' },
];

export const Check: React.FC<{ on: boolean; title: string; sub: string; onChange: (on: boolean) => void }> = ({ on, title, sub, onChange }) =>
    <button type="button" className={`report-modal-check${on ? ' on' : ''}`} role="checkbox" aria-checked={on} onClick={() => onChange(!on)}>
        <span className="box">{on && <FiCheck />}</span>
        <span className="t"><strong>{title}</strong><span>{sub}</span></span>
    </button>;

// the "File type" column: PDF / Excel / CSV radio cards
export const FileTypeColumn: React.FC<{ type: FileType; onChange: (type: FileType) => void }> = ({ type, onChange }) =>
    <div className="col">
        <span className="caption">File type</span>
        {FILE_TYPES.map(f => <button type="button" key={f.key} role="radio" aria-checked={type === f.key}
            className={`report-modal-option${type === f.key ? ' on' : ''}`} onClick={() => onChange(f.key)}>
            <span className="tile">{f.icon}</span>
            <span className="t"><strong>{f.title}</strong><span>{f.sub}</span></span>
            <span className="radio">{type === f.key && <FiCheck />}</span>
        </button>)}
    </div>;

interface DownloadProps { stock: Stock; data: RegistryData; initial: ReportKey; onClose: () => void }

// Figma 550:2576 "Download this report": one button, the file type is chosen inside.
// PDF opens the printable report (real register data) in a new tab; CSV is built in the browser; Excel is preview only.
export const DownloadReportModal: React.FC<DownloadProps> = ({ stock, data, initial, onClose }) => {
    const [type, setType] = useState<FileType>('pdf');
    const [report, setReport] = useState<ReportKey>(initial);
    const [totals, setTotals] = useState(true);
    const [contacts, setContacts] = useState(false);
    const asAt = asAtDate(data.listing);

    const download = () => {
        if (type === 'pdf') {
            window.open(reportUrl(stock, report, totals), '_blank', 'noopener');
            onClose();
            return;
        }
        if (type === 'excel') { toast.info('Preview only: Excel files aren’t built yet — the CSV has the same columns.'); return; }
        if (report === 'board-pack') { toast.info('Preview only: the board pack comes as a PDF — pick PDF, or download one report as CSV.'); return; }
        const lines = reportCsv(stock, data, report, contacts);
        if (!lines) { toast.error('This report has no register data yet.'); return; }
        saveCsv(`${stock.ticker}-${report}.csv`, lines);
        onClose();
    };

    return <ReportModal className="download" icon={<FiDownload />} title="Download This Report"
        sub="One report, three file types — pick whichever suits the person you’re sending it to"
        note="The PDF matches the page in your board pack, so the board sees the same thing." onClose={onClose}
        buttons={<button type="button" className="btn-primary" onClick={download}><FiDownload /> Download</button>}>
        <div className="report-modal-cols">
            <FileTypeColumn type={type} onChange={setType} />
            <div className="col">
                <span className="caption">What to include</span>
                <label className="report-modal-field">
                    <span>Which report</span>
                    <span className="report-modal-select">
                        <select value={report} onChange={e => setReport(e.target.value as ReportKey)}>
                            {[...REPORTS, BOARD_PACK].map(r => <option key={r.key} value={r.key}>{r.title}</option>)}
                        </select>
                        <FiChevronDown />
                    </span>
                </label>
                <label className="report-modal-field">
                    <span>As at</span>
                    <span className="report-modal-select">
                        <select value="latest" onChange={() => undefined}>
                            <option value="latest">Latest sync{asAt ? ` — ${asAt}` : ''}</option>
                        </select>
                        <FiChevronDown />
                    </span>
                </label>
                <Check on={totals} onChange={setTotals} title="Cover page and totals" sub="The summary numbers at the top of the report" />
                <Check on={contacts} onChange={setContacts} title="Holder contact details" sub="Address and email — keep this file secure" />
            </div>
        </div>
    </ReportModal>;
};

// a report other than the Reports tab's (e.g. one holdings-movement day) passes its own subject, subtitle and repeat wording
interface EmailProps {
    stock: Stock; report?: ReportKey; asAt: string | null; onClose: () => void;
    subject?: string; sub?: string; repeat?: { title: string; sub: string };
}

// Figma 532:3236 "Email this report". Sending report emails has no API yet, so Send Report is preview only.
export const EmailReportModal: React.FC<EmailProps> = ({ stock, report = 'board-pack', asAt, onClose, subject: initialSubject, sub, repeat: repeatText }) => {
    const user = useSelector((state: IGlobalState) => state.user);
    const company = stock.companyName || stock.ticker;
    const [sendTo, setSendTo] = useState(user.username.includes('@') ? user.username : '');
    const [subject, setSubject] = useState(initialSubject ?? `${company} — ${reportTitle(report).toLowerCase()}${asAt ? `, ${asAt}` : ''}`);
    const [message, setMessage] = useState('');
    const [repeat, setRepeat] = useState(false);

    const send = () => toast.info('Preview only: emailing reports isn’t connected yet — download the PDF and attach it for now.');

    return <ReportModal className="email" icon={<FiMail />} title="Email This Report" sub={sub ?? 'Sends the report as a PDF attachment'}
        note={`Sent from Investi on behalf of ${company}.`} onClose={onClose}
        buttons={<button type="button" className="btn-primary" onClick={send} disabled={!sendTo.trim()}><FiSend /> Send Report</button>}>
        <label className="field">
            <span className="label">Send to <em>· separate addresses with a comma</em></span>
            <input type="text" className="report-modal-input" value={sendTo} onChange={e => setSendTo(e.target.value)} />
        </label>
        <label className="field">
            <span className="label">Subject</span>
            <input type="text" className="report-modal-input" value={subject} onChange={e => setSubject(e.target.value)} />
        </label>
        <label className="field">
            <span className="label">Message <em>(optional)</em></span>
            <textarea rows={3} value={message} onChange={e => setMessage(e.target.value)} />
        </label>
        <Check on={repeat} onChange={setRepeat} title={repeatText?.title ?? 'Send this every month'} sub={repeatText?.sub ?? 'Investi emails it on the 1st of the month once the register updates'} />
    </ReportModal>;
};
